import * as Sentry from "@sentry/nextjs";

/**
 * Centralized logger that routes all application logging through Sentry.
 *
 * - Errors  → Sentry.captureException / captureMessage (level "error")
 * - Warnings → Sentry.captureMessage (level "warning")
 * - Info / Debug → Sentry.addBreadcrumb (level "info" / "debug")
 * - Security → Sentry.addBreadcrumb (category "security", level "info")
 *
 * All functions accept an optional `context` object that is attached as
 * Sentry "extra" data via `withScope`, giving each event its own context
 * without polluting the global scope.
 */

export type LogContext = Record<string, unknown>;

// ── Errors ──────────────────────────────────────────────────────────────

/**
 * Report an error to Sentry.
 *
 * - If `error` is an `Error` instance it is sent with `captureException`.
 * - Otherwise it is treated as a message string and sent with
 *   `captureMessage` at "error" level.
 */
export function logError(
  error: unknown,
  context?: LogContext,
): void {
  if (process.env.ENV === 'devlocal') {
    console.error('[ERROR]', error, context || '');
  }
  try {
    if (context) {
      Sentry.withScope((scope) => {
        scope.setExtras(context);
        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(String(error), "error");
        }
      });
    } else {
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(String(error), "error");
      }
    }
  } catch {
    // Sentry may not be initialised (e.g. during static build).
    // Swallow so the application keeps running.
  }
}

// ── Warnings ────────────────────────────────────────────────────────────

/**
 * Send a warning-level message to Sentry.
 */
export function logWarning(
  message: string,
  context?: LogContext,
): void {
  try {
    if (context) {
      Sentry.withScope((scope) => {
        scope.setExtras(context);
        Sentry.captureMessage(message, "warning");
      });
    } else {
      Sentry.captureMessage(message, "warning");
    }
  } catch {
    // Graceful degradation during build / uninitialised-SDK scenarios.
  }
}

// ── Informational (breadcrumb) ──────────────────────────────────────────

/**
 * Record an informational breadcrumb in Sentry.
 * These are attached to subsequent error events as context trail.
 */
export function logInfo(
  message: string,
  data?: LogContext,
): void {
  try {
    Sentry.addBreadcrumb({
      category: "app",
      message,
      level: "info",
      ...(data ? { data } : {}),
    });
  } catch {
    // no-op
  }
}

// ── Debug (breadcrumb) ──────────────────────────────────────────────────

/**
 * Record a debug-level breadcrumb in Sentry.
 * Useful for operational / tracing logs (e.g. webhook processing steps).
 */
export function logDebug(
  message: string,
  data?: LogContext,
): void {
  if (process.env.ENV === 'devlocal') {
    console.debug(`[DEBUG] ${message}`, data || '');
  }
  try {
    Sentry.addBreadcrumb({
      category: "app",
      message,
      level: "debug",
      ...(data ? { data } : {}),
    });
  } catch {
    // no-op
  }
}

// ── Security (breadcrumb) ───────────────────────────────────────────────

/**
 * Record a security-related breadcrumb in Sentry.
 * Uses the "security" category so these can be filtered in the Sentry UI.
 */
export function logSecurity(message: string): void {
  try {
    Sentry.addBreadcrumb({
      category: "security",
      message: `[SECURITY] ${message}`,
      level: "info",
    });
  } catch {
    // no-op
  }
}
